import { supabase } from "@/integrations/supabase/client";

export type ProcessingStep =
  | 'metadata_extraction'
  | 'research_overview'
  | 'ai_review';

export const DEFAULT_UPLOAD_STEPS: ProcessingStep[] = [
  'metadata_extraction',
  'research_overview',
  'ai_review',
];

export const ARXIV_IMPORT_STEPS: ProcessingStep[] = [
  'research_overview',
  'ai_review',
];

/**
 * Seed paper_processing_jobs rows for the given paper, idempotent.
 * Call BEFORE invoking the corresponding edge functions so the timeline
 * is visible immediately on the upload-success page.
 */
export async function initProcessingJobs(
  paperId: string,
  steps: ProcessingStep[] = DEFAULT_UPLOAD_STEPS,
): Promise<void> {
  if (!paperId || steps.length === 0) return;
  try {
    const { error } = await supabase.rpc('init_paper_processing_jobs', {
      p_paper_id: paperId,
      p_steps: steps,
    });
    if (error) {
      console.warn('[initProcessingJobs] failed:', error.message);
      return;
    }

    // GDPR objection: if the user has objected to AI processing,
    // mark ai_review as skipped so the timeline reflects reality.
    if (steps.includes('ai_review')) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: prefs } = await supabase
        .from('user_preferences')
        .select('object_to_ai_processing')
        .eq('user_id', user.id)
        .maybeSingle();

      if (prefs?.object_to_ai_processing === true) {
        await supabase.rpc('skip_paper_processing_step', {
          p_paper_id: paperId,
          p_step: 'ai_review',
          p_message: 'Skipped — AI processing objection on file',
        });
      }
    }
  } catch (e) {
    console.warn('[initProcessingJobs] unexpected error:', e);
  }
}

/**
 * Retry a single failed step. Re-queues the row in the DB; the caller is
 * responsible for re-invoking the matching edge function.
 */
export async function retryProcessingStep(
  paperId: string,
  step: ProcessingStep,
): Promise<void> {
  const { error } = await supabase.rpc('retry_paper_processing_step', {
    p_paper_id: paperId,
    p_step: step,
  });
  if (error) throw new Error(error.message);
}
